import React, { useCallback, useState } from 'react';
import { Privilege } from '../../../api';
import useMe from '../../Util/useMe';
import useHasPrivilege from '../../Util/useHasPrivilege';
import { TSession } from '../Sessions';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import AssignmentIcon from '@material-ui/icons/Assignment';
import ClearIcon from '@material-ui/icons/Clear';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  makeStyles,
  Tooltip,
} from '@material-ui/core';
import styled from 'styled-components';
import SelectSingleUser, { UserReference } from '../../Util/SelectSingleUser';
import { TRow, UserFragment } from './sessionLabelOverviewReducer';

const toOwner = (user: UserReference): UserFragment => ({
  id: user.id,
  name: [user.firstName, user.lastName].filter(Boolean).join(' '),
});

type Props = {
  session?: TSession;
  value: TRow;
  onChange: (ownerid: UserFragment | null) => void;
};

const useStyles = makeStyles({
  paper: {
    overflow: 'visible',
  },
});

const SessionLabelOwner = (props: Props) => {
  //   const { data } = useUserQuery({ variables: { id: props.value.owner.id || '' }, skip: !props.value.owner });
  const { data: me } = useMe();
  const canAssignTasksToAnyone = useHasPrivilege(Privilege.AssignLabelTasks);
  const [assign, setAssign] = useState(false);
  const [user, setUser] = useState<UserReference | undefined>(undefined);
  const classes = useStyles();

  const onClaim = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      if (me && me.me) props.onChange({ id: Number(me.me.id), name: [me.me.firstname, me.me.lastname].filter(Boolean).join(' ') });
    },
    [props, me]
  );

  const onUnclaim = useCallback(
    (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      props.onChange(null);
    },
    [props]
  );

  const onAssign = useCallback((evt) => {
    evt.preventDefault();
    evt.stopPropagation();
    setAssign(true);
  }, []);

  const assignToUser = useCallback(() => {
    if (user) {
      props.onChange(toOwner(user));
    }
    setAssign(false);
  }, [user, props]);

  const assigned = props.value.owner ? (props.value.owner.name || props.value.owner.id) + ' ' : '';

  let claimTask;
  if (!props.value.owner && me) {
    claimTask = (
      <Tooltip title="Claim this task">
        <AssignmentIndIcon color="secondary" onClick={onClaim} />
      </Tooltip>
    );
  }

  let unclaimTask;
  if (props.value.owner && me && (canAssignTasksToAnyone || String(props.value.owner.id) === me.me?.id)) {
    unclaimTask = (
      <Tooltip title="Release this task">
        <ClearIcon color="secondary" onClick={onUnclaim} />
      </Tooltip>
    );
  }

  let assignTask;
  if (canAssignTasksToAnyone) {
    assignTask = (
      <Tooltip title="Assign task to user">
        <AssignmentIcon color="secondary" onClick={onAssign} />
      </Tooltip>
    );
  }

  return (
    <Container
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
    >
      {assign && (
        <Dialog
          open={assign}
          classes={{ paper: classes.paper }}
          onClose={() => setAssign(false)}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
        >
          <DialogTitle id="alert-dialog-title">Assign task</DialogTitle>
          <DialogContent style={{ overflow: 'visible' }}>
            <DialogContentText id="alert-dialog-description">
              Select a user to whom you want to assign this task.
            </DialogContentText>
            <SelectSingleUser value={user ? { id: String(user.id) } : (props.value.owner ? { id: String(props.value.owner.id) } : undefined)} onChange={setUser} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setAssign(false)} color="secondary">
              Cancel
            </Button>
            <Button onClick={assignToUser} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      )}
      {assigned} {claimTask} {unclaimTask} {assignTask}
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

export default SessionLabelOwner;
